import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import NwrCallback from "./pages/NwrCallback";
import Dashboard from "./pages/Dashboard";
import Tournaments from "./pages/Tournaments";
import TournamentDetail from "./pages/TournamentDetail";
import Boards, { BoardDetail } from "./pages/Boards";
import Leaderboard from "./pages/Leaderboard";
import Chat from "./pages/Chat";
import AthleteId from "./pages/AthleteId";
import Profile from "./pages/Profile";
import Verify from "./pages/Verify";
import LiveMatches, { LiveMatchDetail } from "./pages/Live";
import MatchAnalysis from "./pages/MatchAnalysis";
import Social, { SocialThread } from "./pages/Social";
import Friends from "./pages/Friends";
import Tactics, { TacticEditor } from "./pages/Tactics";
import Achievements from "./pages/Achievements";
import Tickets, { TicketDetail } from "./pages/Tickets";
import Notifications from "./pages/Notifications";

import AppLayout from "./components/AppLayout";
import AdminLayout from "./components/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminBoards from "./pages/admin/AdminBoards";
import AdminLive from "./pages/admin/AdminLive";
import AdminAchievements from "./pages/admin/AdminAchievements";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTickets from "./pages/admin/AdminTickets";
import { AdminTournaments, AdminLeaderboardClassic, AdminChannels, AdminUsers } from "./pages/admin/AdminCore";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white/60 font-mono text-sm tracking-widest">LOADING…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify/:memberId" element={<Verify />} />
      <Route path="/auth/nwr-callback" element={<NwrCallback />} />

      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/tournaments/:id" element={<TournamentDetail />} />
        <Route path="/boards" element={<Boards />} />
        <Route path="/boards/:id" element={<BoardDetail />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/live" element={<LiveMatches />} />
        <Route path="/live/:id" element={<LiveMatchDetail />} />
        <Route path="/matches/:id/analysis" element={<MatchAnalysis />} />
        <Route path="/social" element={<Social />} />
        <Route path="/social/:id" element={<SocialThread />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/tactics" element={<Tactics />} />
        <Route path="/tactics/:id" element={<TacticEditor />} />
        <Route path="/achievements" element={<Achievements />} />
        <Route path="/support" element={<Tickets />} />
        <Route path="/support/:id" element={<TicketDetail />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat/:channelId" element={<Chat />} />
        <Route path="/dm/:peerId" element={<Chat />} />
        <Route path="/athlete-id" element={<AthleteId />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>

      <Route element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/tournaments" element={<AdminTournaments />} />
        <Route path="/admin/live" element={<AdminLive />} />
        <Route path="/admin/boards" element={<AdminBoards />} />
        <Route path="/admin/leaderboard" element={<AdminLeaderboardClassic />} />
        <Route path="/admin/channels" element={<AdminChannels />} />
        <Route path="/admin/achievements" element={<AdminAchievements />} />
        <Route path="/admin/tickets" element={<AdminTickets />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}
